import type { InventoryItem } from '../types/inventory.types';
import { validateN11RequiredFields } from '../utils/validators';
import { n11Api } from '../../../../config/apiClient';

/**
 * N11 entegrasyon işlemlerini yöneten servis
 */
export class N11Service {
  /**
   * N11 entegrasyon durumunu kontrol eder
   * @param companyId - Şirket ID'si
   * @returns Promise<boolean> - Entegrasyon aktif mi?
   */
  static async checkN11Integration(companyId: string): Promise<boolean> {
    try {
      const response = await n11Api.get('/api/n11/integration/credentials');
      const data = response.data;
      const isActive = data.success === true;
      console.log('🔍 [N11 INTEGRATION] Button State:', isActive);
      return isActive;
    } catch (error) {
      console.error('❌ [N11 INTEGRATION] N11 entegrasyon durumu kontrol edilirken hata:', error);
      return false;
    }
  }

  /**
   * Bir ürünü N11'e gönderir
   * @param companyId - Şirket ID'si
   * @param item - Gönderilecek ürün
   * @param categoryId - N11 kategori ID'si
   * @param brandName - Marka adı
   * @param shipmentTemplate - Kargo şablonu (opsiyonel)
   * @returns Promise<{ success: boolean; taskId?: number; status?: string; reasons?: string[]; error?: string }>
   */
  static async sendProductToN11(
    companyId: string,
    item: InventoryItem,
    categoryId: string,
    brandName: string,
    shipmentTemplate?: string
  ): Promise<{ success: boolean; taskId?: number; status?: string; reasons?: string[]; error?: string }> {
    try {
      // Marka kontrolü - N11 API'si boş marka kabul etmiyor
      if (!brandName || brandName === '-' || brandName.trim().length === 0) {
        throw new Error(
          `Ürün N11'e gönderilemez: Marka bilgisi eksik!\n\nÜrün: ${item.name}\nStok Kodu: ${item.stockCode}\n\nLütfen önce ürüne bir marka atayınız.`
        );
      }

      // API payload'ını hazırla
      const payload = {
        productTitle: item.name,
        description: item.description,
        categoryId: categoryId,
        brandName: brandName,
        stockCode: item.stockCode,
        barcode: item.barcode || null,
        // ✅ YENİ SİSTEM: WarehouseStocks'tan toplam hesapla
        quantity: item.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0,
        salePrice: item.salePrice,
        listPrice: (item as any).listPrice || item.salePrice,
        vatRate: item.vatRate || 18,
        preparingDay: 2,
        shipmentTemplate: shipmentTemplate || 'BAREM',
        maxPurchaseQuantity: 10,
        images: item.imageUrl ? item.imageUrl.split(',').filter(Boolean) : [],
        attributes: []
      };

      console.log('🚀 [N11 SEND PRODUCT] Payload:', payload);

      // Backend API'ye istek gönder
      const response = await n11Api.post('/api/n11/products/save', payload);
      const data = response.data;

      if (!data.success) {
        throw new Error(data.error || 'Ürün gönderme işlemi başarısız');
      }

      console.log('✅ [N11 SEND PRODUCT] Ürün gönderme başarılı:', data);

      return {
        success: true,
        taskId: data.taskId,
        status: data.status,
        reasons: data.reasons
      };
    } catch (error) {
      console.error('❌ [N11 SEND PRODUCT] Ürün gönderme hatası:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Ürün gönderme sırasında bir hata oluştu'
      };
    }
  }

  /**
   * N11 zorunlu alanlarını kontrol eder
   * @param item - Kontrol edilecek ürün
   * @param getBrandName - Marka adı getiren fonksiyon
   * @returns { isValid: boolean; missingFields: string[] }
   */
  static validateN11Product(
    item: InventoryItem,
    getBrandName: (id: string | null) => string
  ): { isValid: boolean; missingFields: string[] } {
    return validateN11RequiredFields(item, getBrandName);
  }

  /**
   * N11 log kayıt formatını hazırlar
   * @param type - Log tipi
   * @param productName - Ürün adı
   * @param stockCode - Stok kodu
   * @param taskId - N11 task ID (number)
   * @param status - İşlem durumu
   * @param operation - İşlem açıklaması
   * @param details - Ek detaylar
   * @returns Log objesi
   */
  static prepareN11LogData(
    type: 'price-stock-update' | 'product-create' | 'product-save',
    productName: string,
    stockCode: string,
    taskId: number,
    status: 'IN_QUEUE' | 'PROCESSED' | 'REJECT',
    operation: string,
    details: any
  ): {
    type: 'price-stock-update' | 'product-create' | 'product-save';
    productName: string;
    stockCode: string;
    taskId: number;
    status: 'IN_QUEUE' | 'PROCESSED' | 'REJECT';
    operation: string;
    details: any;
  } {
    return {
      type,
      productName,
      stockCode,
      taskId,
      status,
      operation,
      details
    };
  }

  /**
   * N11 başarı mesajını formatlar
   * @param productName - Ürün adı
   * @param taskId - Task ID (number)
   * @param status - Durum
   * @param reasons - Nedenler
   * @returns Formatlanmış mesaj
   */
  static formatSuccessMessage(
    productName: string,
    taskId: number,
    status: string,
    reasons?: string[]
  ): string {
    return `${productName} başarıyla N11'e gönderildi!
      
Task ID: ${taskId}
Status: ${status}
${reasons ? reasons.join(', ') : ''}

Ürün işleme alındı. Durumunu N11 satıcı panelinden kontrol edebilirsiniz.`;
  }

  /**
   * N11 ile ürün fiyat/stok senkronizasyonu
   * @param companyId - Şirket ID
   * @param item - Senkronize edilecek ürün
   * @param field - Güncellenecek alan ('quantity' veya 'salePrice')
   * @param newValue - Yeni değer
   * @returns Promise<{ taskId: number; status: string; reasons?: string[] }>
   */
  static async syncProductWithN11(
    companyId: string,
    item: any, // InventoryItem
    field: 'quantity' | 'salePrice',
    newValue: number
  ): Promise<{ success: boolean; taskId?: number; status?: string; reasons?: string[]; error?: string }> {
    if (!companyId || !item.stockCode) {
      throw new Error('Şirket ID veya stok kodu eksik');
    }

    console.log('🔄 [N11 SYNC] Ürün senkronizasyonu başlatılıyor:', {
      stockCode: item.stockCode,
      field,
      newValue
    });

    try {
      // API payload'ını hazırla
      const updateData = {
        products: [{
          stockCode: item.stockCode,
          ...(field === 'quantity' && { quantity: newValue }),
          ...(field === 'salePrice' && { 
            salePrice: newValue, // TL cinsinden
            listPrice: (item.listPrice || newValue)  // Liste fiyatı varsa onu kullan, yoksa satış fiyatını
          })
        }]
      };

      console.log('📤 [N11 SYNC] API Payload:', updateData);

      // Backend API'ye istek gönder
      const response = await n11Api.post('/api/n11/products/update-price-stock', updateData);
      const result = response.data;

      if (!result.success) {
        throw new Error(result.error || 'N11 güncelleme işlemi başarısız');
      }

      console.log('✅ [N11 SYNC] Senkronizasyon başarılı:', result);
      
      return {
        success: true,
        taskId: result.taskId,
        status: result.status,
        reasons: result.reasons
      };
    } catch (error) {
      console.error('❌ [N11 SYNC] Senkronizasyon hatası:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'N11 senkronizasyonu sırasında bir hata oluştu'
      };
    }
  }

  /**
   * N11 senkronizasyon başarı mesajını formatla
   * @param item - Ürün
   * @param field - Güncellenecek alan
   * @param newValue - Yeni değer
   * @param taskId - Task ID
   * @param status - Durum
   * @param reasons - Nedenler
   * @returns Formatlanmış mesaj
   */
  static formatSyncSuccessMessage(
    item: any,
    field: 'quantity' | 'salePrice',
    newValue: number,
    taskId: number,
    status: string,
    reasons?: string[]
  ): string {
    return `N11 senkronizasyonu tamamlandı!
    
Task ID: ${taskId}
Status: ${status}
${reasons ? reasons.join(', ') : ''}

Ürün: ${item.name}
${field === 'quantity' ? `Yeni Miktar: ${newValue}` : `Yeni Fiyat: ${newValue} TL`}`;
  }
}

